import { MonetizationType } from '@/@types/graphql-types';
import { TitleOfferFragment } from '@/components/buybox/graphql/fragments/Offer.fragment';
import { WebLocale } from '@/enums/web-locale';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { MaybeRefOrGetter, toValue } from '@vueuse/core';
import { useTitleDetailsStore } from './composables/useStores';
import { computed, onMounted, onServerPrefetch } from 'vue';

type PromotionProviders = 9 | 119 | 337 | 1773 | 1899 | 531 | 350;

const promotionProviderIcons: Record<PromotionProviders, string> = {
	9: `${JW_CONFIG.IMAGESCALER_URL}/icon/52449539/s100/amazonprime.webp`,
	119: `${JW_CONFIG.IMAGESCALER_URL}/icon/52449539/s100/amazonprime.webp`,
	337: `${JW_CONFIG.IMAGESCALER_URL}/icon/313118777/s100/disneyplus.webp`,
	1773: `${JW_CONFIG.IMAGESCALER_URL}/icon/300772362/s100/skyshowtime.webp`,
	1899: `${JW_CONFIG.IMAGESCALER_URL}/icon/305458112/s100/max.webp`,
	531: `${JW_CONFIG.IMAGESCALER_URL}/icon/242706661/s100/paramountplus.webp`,
	350: `${JW_CONFIG.IMAGESCALER_URL}/icon/152862153/s100/appletvplus.webp`,
};

/** Provider promotions are only for titles with Flatrate and free offers of the promo provider. */
const flatrateOrFree = [MonetizationType.Flatrate, MonetizationType.Free];

interface PromoText {
	/** Set `null` to disable. */
	ribbon: string | null;
	/** Set `null` to disable. */
	offerText: string | null;
	/** Set `null` to disable. */
	longText: string | null;
	/** Optional promotion start date in `YYYY-MM-DD` format. */
	startDate?: ISO8601;
	/** `YYYY-MM-DD` format. */
	endDate: ISO8601;
	/** The ID of the promoted provider. */
	provider: PromotionProviders;
	/** The Url of the promoted provider which overrides the regular offer link. */
	url?: string;
}

/** Normal PromoText config with the Provider Icon URL */
export type PromoTextWithIcon = PromoText & { icon: string };

type PromotionCountries = 'AR' | 'CL' | 'MX' | 'ES';

const promotions: Record<PromotionCountries, PromoText[]> = {
	ES: [
		{
			ribbon: null,
			offerText: null,
			longText: 'Ve a SkyShowtime ahora',
			startDate: '2025-05-09',
			endDate: '2025-06-30',
			provider: 1773,
		},
	],
	MX: [
		{
			ribbon: 'NUEVO',
			offerText: 'Desde MXN 149',
			longText: 'Un plan ideal para ti.',
			startDate: '2025-04-25',
			endDate: '2025-05-24',
			provider: 337,
		},
	],
	AR: [
		{
			ribbon: 'NUEVO',
			offerText: 'Desde $9.99',
			longText: 'Un plan ideal para vos.',
			startDate: '2025-04-25',
			endDate: '2025-05-24',
			provider: 337,
		},
	],
	CL: [
		{
			ribbon: 'NUEVO',
			offerText: 'Desde CLP 8.490',
			longText: 'Un plan ideal para ti.',
			startDate: '2025-04-25',
			endDate: '2025-05-24',
			provider: 337,
		},
	],
};

/** SSR Safe way to get active provider promotions. */
export function useProviderPromotion(title?: MaybeRefOrGetter<TitleDetail>) {
	const { setProviderPromotion, providerPromotion } = useTitleDetailsStore();

	onServerPrefetch(async () => await setProviderPromotion(toValue(title)));

	// refetch on client (no hydration error) to get the updated status
	onMounted(async () => await setProviderPromotion(toValue(title)));

	const titleHasProviderPromotion = computed(() => providerPromotion.value != null);

	function isPromotionOffer(offer: TitleOfferFragment) {
		return (
			flatrateOrFree.includes(offer.monetizationType) &&
			(providerPromotion.value as PromoTextWithIcon | null)?.provider === offer.package.packageId
		);
	}

	return {
		/** Is a provider promotion applicable to the current title. */
		titleHasProviderPromotion,

		/** Check if the offer is under the current title's provider promotion. */
		isPromotionOffer,

		/** The applicable promotion itself. */
		providerPromotion,
	};
}

export function getPromoText(
	country: string,
	title: TitleDetail | null = null,
	currentDate: Date = new Date()
): PromoTextWithIcon | null {
	const texts = promotions[country as PromotionCountries];

	if (!texts || texts.length === 0 || title == null || title.offers.length === 0) return null;

	const promoText = texts.find(promo =>
		title.offers.some(
			offer => flatrateOrFree.includes(offer.monetizationType) && offer.package.packageId === promo.provider
		)
	);

	if (promoText == null) return null;
	// Checking if we're within the promo dates
	if (promoText.startDate && +currentDate < new Date(promoText.startDate).setUTCHours(0, 0, 0, 0)) return null;
	if (promoText.endDate && +currentDate > new Date(promoText.endDate).setUTCHours(23, 59, 59, 999)) return null;

	return { ...promoText, icon: promotionProviderIcons[promoText.provider] };
}

// Disney External Pixel Tracking
const oceaniaLocales = new Set([WebLocale.au, WebLocale.nz]);
const noramLocales = new Set([WebLocale.us, WebLocale.ca]);
const latamLocales = new Set([
	WebLocale.br,
	WebLocale.mx,
	WebLocale.ar,
	WebLocale.cl,
	WebLocale.co,
	WebLocale.pe,
	WebLocale.uy,
	WebLocale.ec,
	WebLocale.pa,
	WebLocale.cr,
	WebLocale.py,
	WebLocale.bo,
	WebLocale.gt,
	WebLocale.ni,
	WebLocale.do,
	WebLocale.sv,
	WebLocale.hn,
	WebLocale.ve,
]);

export function getDisneyImpressionUrl(packageId: number | null | undefined, webLocale: WebLocale) {
	if (packageId === 337 /* Disney+ */) {
		if (oceaniaLocales.has(webLocale) || noramLocales.has(webLocale))
			return 'https://imp.pxf.io/i/1206980/1833790/9358';

		if (latamLocales.has(webLocale)) return 'https://imp.pxf.io/i/1206980/1848147/9358';
	}

	if (packageId === 619 /* Star+ */) {
		if (latamLocales.has(webLocale)) return 'https://imp.pxf.io/i/1206980/1584316/13591';
	}

	return null;
}

// TYPES
type digits = '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9';
type day = `${'0'}${Exclude<digits, '0'>}` | `${'1' | '2'}${digits}` | '30' | '31';
type month = `${'0'}${Exclude<digits, '0'>}` | '10' | '11' | '12';
type year = `20${digits}${digits}`;
type ISO8601 = `${year}-${month}-${day}`;
